import React, { useEffect, useMemo, useState } from "react";
import { Card, Select, Space, Button, message, Tag } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { scCustodyLedger, scGetParties, scGetItems } from "../../api/client";
import { byStr, byNum } from "../../utils/sorters";
import { JALALI_MONTHS, JALALI_YEARS, currentJalali } from "../../utils/dateUtils";
import { CATEGORY_LABEL, fmtKg, PARTY_TYPE_LABEL } from "./scConstants";
import { enabledCategories } from "./supplyCategories";
import GxTable from "../../components/GxTable";

const { Option } = Select;

/** طرف‌حساب‌های مرتبط با زنجیرهٔ فعلی (تأمین، بافندگی، رنگرزی، انبار) */
const CUSTODY_PARTY_TYPES = new Set([
  "supplier", "knitting_workshop", "dyeing_provider", "internal_warehouse",
]);

export default function CustodyLedgerPage() {
  const cj = currentJalali();
  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ party_id: null, item_type_id: null, year: null, month: null, category: null });

  useEffect(() => {
    scGetParties().then((r) => setParties(r.data || [])).catch(() => {});
    scGetItems().then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  const custodyParties = useMemo(
    () => (parties || []).filter((p) => CUSTODY_PARTY_TYPES.has(p.party_type)),
    [parties],
  );

  // فقط کالاهایی که در ماندهٔ فعلی ظاهر شده‌اند یا هنوز فعال‌اند
  const itemOptions = useMemo(() => {
    const used = new Set(rows.map((r) => r.item_type_id).filter(Boolean));
    return (items || []).filter((i) => used.has(i.id) || i.is_active !== false);
  }, [items, rows]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => {
        if (v && k !== "category") params[k] = v;
      });
      const r = await scCustodyLedger(params);
      let data = r.data || [];
      if (f.category) {
        data = data.filter((row) => row.item_category === f.category);
      }
      setRows(data);
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در بارگذاری"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [JSON.stringify(f)]);

  const columns = [
    { title: "طرف", dataIndex: "party_name", sorter: byStr("party_name"),
      gxFilter: "enum",
      gxFilterOptions: custodyParties.map((p) => ({ text: p.name, value: p.name })) },
    { title: "کالا", dataIndex: "item_category", sorter: byStr("item_category"),
      gxFilter: "enum",
      gxFilterOptions: Object.entries(CATEGORY_LABEL).map(([v, l]) => ({ text: l, value: v })),
      render: (v, r) => <Tag>{CATEGORY_LABEL[v] || r.item_name || v}</Tag> },
    { title: "دریافت‌شده", dataIndex: "received_kg", align: "center", sorter: byNum("received_kg"),
      gxFilter: "num", render: (v) => fmtKg(v) },
    { title: "ارسال‌شده", dataIndex: "sent_kg", align: "center", sorter: byNum("sent_kg"),
      gxFilter: "num", render: (v) => fmtKg(v) },
    { title: "اصلاحیه", dataIndex: "adjustment_kg", align: "center", gxFilter: "num",
      render: (v) => (v ? fmtKg(v) : "—") },
    {
      title: "ماندهٔ فعلی (امانی)", dataIndex: "current_balance_kg", align: "center", sorter: byNum("current_balance_kg"),
      gxFilter: "num",
      render: (v) => <b style={{ color: v > 0 ? "#107C41" : v < 0 ? "#cf1322" : "#888" }}>{fmtKg(v)}</b>,
    },
  ];

  const custodySummary = [
    { dataIndex: "party_name", type: "unique" },
    { dataIndex: "item_category", type: "unique", labelOf: (v) => CATEGORY_LABEL[v] || v },
    { dataIndex: "received_kg", type: "sum", format: (v) => fmtKg(v) },
    { dataIndex: "sent_kg", type: "sum", format: (v) => fmtKg(v) },
    { dataIndex: "adjustment_kg", type: "sum", format: (v) => fmtKg(v) },
    { dataIndex: "current_balance_kg", type: "sum", format: (v) => fmtKg(v) },
  ];

  // گزینه‌های دسته کالا از رجیستری Canonical + CATEGORY_LABEL
  const categoryFilterOpts = useMemo(() => {
    const cats = new Set(Object.keys(CATEGORY_LABEL));
    rows.forEach((r) => { if (r.item_category) cats.add(r.item_category); });
    return [...cats].map((k) => ({ value: k, label: CATEGORY_LABEL[k] || k }));
  }, [rows]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>دفتر امانی</h2>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>تازه‌سازی</Button>
      </div>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Select allowClear placeholder="طرف حساب" style={{ width: 200 }} showSearch optionFilterProp="children"
            value={f.party_id}
            onChange={(v) => setF((s) => ({ ...s, party_id: v }))}>
            {custodyParties.map((p) => (
              <Option key={p.id} value={p.id}>
                {p.name}
                {PARTY_TYPE_LABEL[p.party_type] ? ` (${PARTY_TYPE_LABEL[p.party_type]})` : ""}
              </Option>
            ))}
          </Select>
          <Select allowClear placeholder="نوع کالا" style={{ width: 160 }}
            value={f.item_type_id}
            showSearch optionFilterProp="children"
            onChange={(v) => setF((s) => ({ ...s, item_type_id: v }))}>
            {itemOptions.map((i) => <Option key={i.id} value={i.id}>{i.name}</Option>)}
          </Select>
          <Select allowClear placeholder="دسته کالا" style={{ width: 140 }}
            value={f.category}
            onChange={(v) => setF((s) => ({ ...s, category: v }))}
            options={categoryFilterOpts} />
          <Select allowClear placeholder="سال" style={{ width: 100 }}
            value={f.year}
            onChange={(v) => setF((s) => ({ ...s, year: v }))}>
            {JALALI_YEARS.map((y) => <Option key={y} value={y}>{y}</Option>)}
          </Select>
          <Select allowClear placeholder="ماه" style={{ width: 110 }}
            value={f.month}
            onChange={(v) => setF((s) => ({ ...s, month: v }))}>
            {JALALI_MONTHS.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
          </Select>
        </Space>
        <div style={{ marginTop: 8, fontSize: 12, color: "#8c8c8c" }}>
          ساختار فعلی: {enabledCategories().map((c) => c.label).join(" · ")}
          {" — "}مانده اول دوره، اصلاحیه و ضایعات در رویدادها ثبت می‌شوند.
        </div>
      </Card>
      <Card>
        <GxTable dataSource={rows} columns={columns}
          layoutId="sc-custody"
          rowKey={(r) => `${r.party_id}-${r.item_type_id}`}
          size="small" loading={loading} pagination={rows.length > 20 ? { pageSize: 20 } : false}
          scroll={{ x: "max-content" }}
          gxSummary={custodySummary} />
      </Card>
    </div>
  );
}
